import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const CarouselWrapper = styled.div`
  width: 100%;
  margin: 3rem auto;
  max-width: 90vw;
  position: relative;

  @media (min-width: 1536px) {
    max-width: 1600px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f5f5f5;
`;

const ImageSlide = styled.div`
  width: 100%;
  height: auto;
  display: ${props => props.$active ? 'block' : 'none'};
  opacity: ${props => props.$active ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 0.75rem;
  display: block;

  &.mobile-only {
    display: block;
    @media (min-width: 768px) {
      display: none;
    }
  }

  &.desktop-only {
    display: none;
    @media (min-width: 768px) {
      display: block;
    }
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  
  @media (min-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const SlideIndicators = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const Indicator = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
`;

const PromoCarousel = ({ images = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slides based on display duration
  useEffect(() => {
    if (images.length > 1) {
      const currentImage = images[currentSlide];
      const duration = (currentImage?.displayDuration || 5) * 1000;

      const timer = setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % images.length);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [currentSlide, images]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <CarouselWrapper>
      <CarouselContainer>
        {images.map((image, index) => (
          <ImageSlide key={index} $active={index === currentSlide}>
            {image.mobileUrl ? (
              <>
                <SlideImage
                  src={image.mobileUrl}
                  alt={`Promo slide ${index + 1}`}
                  className="mobile-only"
                  loading="lazy"
                />
                <SlideImage
                  src={image.desktopUrl}
                  alt={`Promo slide ${index + 1}`}
                  className="desktop-only"
                  loading="lazy"
                />
              </>
            ) : (
              <SlideImage
                src={image.desktopUrl}
                alt={`Promo slide ${index + 1}`}
                loading="lazy"
              />
            )}
          </ImageSlide>
        ))}

        {images.length > 1 && (
          <>
            <ArrowButton direction="left" onClick={prevSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </ArrowButton>
            <ArrowButton direction="right" onClick={nextSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </ArrowButton>
            <SlideIndicators>
              {images.map((_, index) => (
                <Indicator
                  key={index}
                  $active={index === currentSlide}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </SlideIndicators>
          </>
        )}
      </CarouselContainer>
    </CarouselWrapper>
  );
};

export default PromoCarousel;
